<template>
  <div class="glb-upload-page" v-loading="loading">
    <div class="page-header">
        <div>
          <div class="page-title">上传GLB花材</div>
          <div class="page-subtitle">
            先确认名称、分类和模型文件，并完成 GLB 三点标定；发布后会直接进入花材列表。
          </div>
        </div>
      <el-tag type="success" effect="plain">上传与标定已接通</el-tag>
    </div>

    <el-alert
      title="当前页面已支持 GLB 模型上传、三点标定和发布入库；保存草稿仍只保留前端交互。"
      type="info"
      :closable="false"
      show-icon
      class="page-alert"
    >
    </el-alert>

    <div class="content-grid">
      <el-card shadow="never" class="form-card">
        <div slot="header" class="card-title">基础信息</div>
        <el-form
          ref="form"
          :model="form"
          :rules="rules"
          label-width="110px"
          class="upload-form"
        >
          <el-form-item label="名称" prop="name">
            <el-input
              v-model.trim="form.name"
              clearable
              maxlength="40"
              show-word-limit
              placeholder="请输入花材名称"
            >
            </el-input>
          </el-form-item>

          <el-form-item label="选择颜色">
            <el-radio-group v-model="form.colorClassifyId" class="color-radio-group">
              <el-radio :label="item.id" v-for="item in colorList" :key="item.id">
                <span
                  class="color-dot"
                  :style="{ background: item.color }"
                ></span>
                {{ item.name }}
              </el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="生长阶段">
            <el-select
              v-model="form.growthStage"
              clearable
              placeholder="请选择生长阶段，不填也可以"
              class="full-width"
            >
              <el-option label="长苗" value="长苗"> </el-option>
              <el-option label="成株" value="成株"> </el-option>
              <el-option label="开花" value="开花"> </el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="分类" prop="classifyId">
            <el-cascader
              ref="classifyCascader"
              v-model="form.classifyId"
              :options="labelList"
              :show-all-levels="false"
              clearable
              filterable
              class="full-width"
              placeholder="请选择花材分类"
              :props="{
                children: 'child',
                value: 'id',
                label: 'name',
              }"
              @change="handleClassifyChange"
            >
            </el-cascader>
          </el-form-item>

          <el-form-item label="模型文件" prop="modelFilePath">
            <el-radio-group
              v-model="modelSourceType"
              size="mini"
              class="model-source-switch"
              @change="handleModelSourceTypeChange"
            >
              <el-radio-button label="upload">本地上传到 OSS</el-radio-button>
              <el-radio-button label="url">直接填写 OSS 地址</el-radio-button>
            </el-radio-group>

            <el-upload
              v-if="modelSourceType === 'upload'"
              drag
              action=""
              accept=".glb"
              :show-file-list="false"
              class="upload-dropzone"
              :before-upload="handleModelFileUpload"
            >
              <div v-if="!form.modelFileName" class="dropzone-empty">
                <i class="el-icon-upload upload-icon"></i>
                <div class="upload-title">拖拽 GLB 文件到这里，或点击选择文件</div>
                <div class="upload-desc">
                  支持 `.glb` 格式，选择后会上传到当前 OSS 存储
                </div>
              </div>
              <div v-else class="dropzone-filled">
                <i class="el-icon-document file-icon"></i>
                <div class="file-meta">
                  <div class="file-name">{{ form.modelFileName }}</div>
                  <div class="file-desc">
                    <span>{{ modelSizeText }}</span>
                    <span>{{ uploadStatusText }}</span>
                  </div>
                </div>
                <el-tag
                  size="mini"
                  :type="compressing || uploading ? 'warning' : form.modelFilePath ? 'success' : 'info'"
                >
                  {{
                    compressing
                      ? "压缩中"
                      : uploading
                        ? "上传中"
                        : form.modelFilePath
                          ? "已上传"
                          : "待上传"
                  }}
                </el-tag>
              </div>
            </el-upload>

            <div v-else class="model-url-panel">
              <el-input
                v-model.trim="modelUrlInput"
                clearable
                placeholder="请输入 OSS 上的 .glb 地址，支持完整 URL 或相对路径"
                @keyup.enter.native="applyModelUrl"
              >
                <template slot="append">
                  <el-button type="primary" @click="applyModelUrl">应用地址</el-button>
                </template>
              </el-input>
              <div class="model-url-tip">
                可直接粘贴 OSS 完整地址，或仅填写对象路径，例如 `common/demo/example.glb`。
              </div>
            </div>

            <div class="file-actions" v-if="form.modelFileName || modelUrlInput">
              <el-button
                v-if="modelSourceType === 'upload'"
                type="text"
                @click="triggerFilePicker"
              >
                重新选择
              </el-button>
              <el-button
                v-if="modelSourceType === 'url'"
                type="text"
                @click="applyModelUrl"
              >
                重新应用
              </el-button>
              <el-button type="text" class="danger-text" @click="removeModel">
                清空模型
              </el-button>
            </div>

            <div v-if="compressing || uploading" class="upload-progress">
              <i class="el-icon-loading"></i>
              <span>
                {{
                  compressing
                    ? "模型文件正在压缩，请稍候"
                    : "模型文件正在上传，请稍候"
                }}
              </span>
            </div>

            <div v-if="form.modelFilePath" class="uploaded-path">
              <span class="uploaded-path__label">
                {{ modelSourceType === "url" ? "模型地址" : "上传地址" }}
              </span>
              <span class="uploaded-path__value">{{ form.modelFilePath }}</span>
            </div>

            <div v-if="modelSourceType === 'upload'" class="compression-config">
              <div class="compression-config__label">压缩策略</div>
              <el-radio-group
                v-model="compressionPreset"
                size="mini"
                :disabled="uploading || compressing"
              >
                <el-radio-button
                  v-for="item in compressionPresetOptions"
                  :key="item.value"
                  :label="item.value"
                >
                  {{ item.label }}
                </el-radio-button>
              </el-radio-group>
              <div class="compression-config__desc">
                {{ currentCompressionPreset.description }}，仅在模型超过 10MB 且选择“压缩后上传”时生效。
              </div>
            </div>
          </el-form-item>

          <el-form-item label="缩略图" prop="coverImage">
            <div class="cover-upload-row">
              <el-upload
                action=""
                accept=".png,.jpg,.jpeg"
                :show-file-list="false"
                class="cover-upload"
                :before-upload="handleCoverUpload"
              >
                <div v-if="form.coverImage" class="cover-filled">
                  <img
                    :src="form.coverImage | imgFormat"
                    alt="GLB 花材缩略图"
                    class="cover-image"
                  />
                  <div class="cover-mask">点击重新上传</div>
                </div>
                <div v-else class="cover-empty">
                  <i class="el-icon-picture-outline"></i>
                  <div class="cover-empty__title">上传缩略图</div>
                  <div class="cover-empty__desc">前端编辑器会用这张图做素材封面</div>
                </div>
              </el-upload>

              <div class="cover-side">
                <div class="cover-side__title">推荐比例</div>
                <div class="cover-side__desc">
                  建议上传清晰的正方形图片，例如 800 x 800，用于编辑器素材面板展示。
                </div>
                <div v-if="coverUploading" class="upload-progress">
                  <i class="el-icon-loading"></i>
                  <span>缩略图正在上传，请稍候</span>
                </div>
                <div v-else class="cover-side__status">
                  {{ form.coverImage ? "缩略图已上传" : "支持 png / jpg / jpeg，建议不超过 10M" }}
                </div>
                <div v-if="form.coverImage" class="file-actions">
                  <el-button type="text" class="danger-text" @click="removeCover">
                    移除缩略图
                  </el-button>
                </div>
              </div>
            </div>
          </el-form-item>

          <el-form-item label="坐标标定">
            <div class="calibration-panel">
              <div class="calibration-status">
                <el-tag :type="form.modelConfigJson ? 'success' : 'warning'" size="mini">
                  {{ form.modelConfigJson ? "已完成标定" : "待标定" }}
                </el-tag>
                <span class="calibration-status__text">
                  {{
                    form.modelFilePath
                      ? form.modelConfigJson
                        ? "三点标定参数已生成，可直接用于编辑器加载。"
                        : "请打开标定弹窗，为当前 GLB 生成 modelConfigJson。"
                      : "请先上传 GLB 文件，再进行三点标定。"
                  }}
                </span>
              </div>

              <div class="calibration-actions">
                <el-button
                  type="primary"
                  size="mini"
                  :disabled="!form.modelFilePath || uploading"
                  @click="openCalibration"
                >
                  {{ form.modelConfigJson ? "重新标定" : "打开标定弹窗" }}
                </el-button>
                <el-button
                  v-if="form.modelConfigJson"
                  size="mini"
                  @click="clearCalibration"
                >
                  清空标定
                </el-button>
              </div>

              <el-input
                v-if="form.modelConfigJson"
                type="textarea"
                :rows="8"
                readonly
                :value="prettyModelConfigJson"
                class="model-config-preview"
              >
              </el-input>
            </div>
          </el-form-item>

          <el-form-item label="光感调节">
            <div class="appearance-panel">
              <div class="appearance-panel__status">
                <el-tag
                  :type="isDefaultModelAppearance ? 'info' : 'success'"
                  size="mini"
                >
                  {{ isDefaultModelAppearance ? "使用默认光感" : "已烘焙默认光感" }}
                </el-tag>
                <span class="appearance-panel__text">
                  {{
                    form.modelFilePath
                      ? "这里会把当前 GLB 的默认亮度和材质参数直接烘焙进模型文件本身，后续在别的编辑器里打开也能明显看到变化。"
                      : "请先上传 GLB 模型，再打开光感调节。"
                  }}
                </span>
              </div>

              <div class="appearance-panel__actions">
                <el-button
                  type="primary"
                  size="mini"
                  :disabled="!form.modelFilePath || uploading || compressing || appearanceSaving"
                  @click="openAppearanceEditor"
                >
                  {{ isDefaultModelAppearance ? "打开光感调节" : "重新调整光感" }}
                </el-button>
                <el-button
                  v-if="!isDefaultModelAppearance"
                  size="mini"
                  :disabled="appearanceSaving"
                  @click="resetModelAppearance"
                >
                  恢复默认
                </el-button>
              </div>

              <div v-if="appearanceSaving" class="upload-progress">
                <i class="el-icon-loading"></i>
                <span>正在烘焙并上传 GLB 默认光感，请稍候</span>
              </div>

              <div class="appearance-summary">
                <el-tag
                  v-for="item in modelAppearanceSummary"
                  :key="item.label"
                  size="mini"
                  effect="plain"
                >
                  {{ item.label }} {{ item.value }}
                </el-tag>
              </div>
            </div>
          </el-form-item>
        </el-form>
      </el-card>

      <el-card shadow="never" class="preview-card">
        <div slot="header" class="card-title">补充信息</div>
        <div class="extra-panel">
          <div class="extra-section-title">基础信息</div>
          <el-form label-width="110px" class="extra-form">
            <el-form-item label="基础信息">
              <el-input
                v-model="form.basicInfo"
                type="textarea"
                :rows="5"
                placeholder="百度搜索信息填写，什么科什么的。"
              >
              </el-input>
            </el-form-item>
            <el-form-item label="文化内涵">
              <el-input
                v-model="form.connotation"
                placeholder="玫瑰代表爱情"
              >
              </el-input>
            </el-form-item>
            <el-form-item label="日常使用搭配">
              <el-input
                v-model="form.collocation"
                placeholder="玫瑰搭配百合"
              >
              </el-input>
            </el-form-item>
          </el-form>

          <div class="extra-section-title">比赛使用建议</div>
          <el-form label-width="110px" class="extra-form">
            <el-form-item label="色彩搭配建议">
              <el-input
                v-model="form.colorSuggestion"
                type="textarea"
                :rows="3"
                placeholder="花材为黄色，建议搭配相近色系或"
              >
              </el-input>
            </el-form-item>
            <el-form-item label="技术使用建议">
              <el-input
                v-model="form.technicalSuggestion"
                type="textarea"
                :rows="3"
                placeholder="玫瑰可以使用铜丝压弯如不小心剪断后可以用铁丝固定"
              >
              </el-input>
            </el-form-item>
            <el-form-item label="寓意">
              <el-input
                v-model="form.allegory"
                type="textarea"
                :rows="3"
                placeholder="玫瑰象征着爱情，或热烈，或含蓄"
              >
              </el-input>
            </el-form-item>
            <el-form-item label="构图建议">
              <el-input
                v-model="form.compositionSuggestion"
                type="textarea"
                :rows="3"
                placeholder="玫瑰叶大，花大，适合在在怎"
              >
              </el-input>
            </el-form-item>
          </el-form>
        </div>
      </el-card>
    </div>

    <div class="actions">
      <el-button type="primary" :loading="submitting && submitAction === 'save'" @click="handleSubmit('save')">
        保存草稿
      </el-button>
      <el-button type="success" :loading="submitting && submitAction === 'publish'" @click="handleSubmit('publish')">
        发布到花材列表
      </el-button>
      <el-button @click="resetForm">重置</el-button>
    </div>

    <glb-anchor-calibration
      ref="glbCalibration"
      @confirm="handleCalibrationConfirm"
    />
    <glb-appearance-editor
      ref="glbAppearanceEditor"
      @confirm="handleAppearanceConfirm"
    />
    <el-dialog
      title="选择压缩模式"
      :visible.sync="compressionModeDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      @close="cancelCompressionModeDialog"
    >
      <div class="compression-dialog-body">
        <div class="compression-dialog-tip">
          当前模型约
          {{
            pendingCompressionFile
              ? `${(pendingCompressionFile.size / 1024 / 1024).toFixed(2)} MB`
              : "--"
          }}
          ，请选择本次上传要使用的压缩模式。
        </div>
        <el-radio-group
          v-model="compressionPreset"
          class="compression-dialog-options"
        >
          <el-radio
            v-for="item in compressionPresetOptions"
            :key="item.value"
            :label="item.value"
            border
            class="compression-dialog-option"
          >
            <div class="compression-dialog-option__title">{{ item.label }}</div>
            <div class="compression-dialog-option__desc">{{ item.description }}</div>
          </el-radio>
        </el-radio-group>
      </div>
      <div slot="footer">
        <el-button @click="cancelCompressionModeDialog">取消</el-button>
        <el-button type="primary" @click="confirmCompressionModeDialog">
          开始压缩并上传
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import GlbAnchorCalibration from "../../components/GlbAnchorCalibration.vue";
import GlbAppearanceEditor from "../../components/GlbAppearanceEditor.vue";
import {
  createDefaultGlbColorAdjust,
  isDefaultGlbColorAdjust,
  normalizeGlbColorAdjust,
} from "../../utils/glbAppearance";
import {
  compressGlbFile,
  getGlbCompressionPreset,
  GLB_COMPRESSION_PRESETS,
} from "../../utils/glbCompression";

const fallbackLabelList = [
  {
    id: "demo-flower-cut",
    name: "鲜切花",
    child: [
      {
        id: "demo-flower-cut-rose",
        name: "玫瑰",
      },
      {
        id: "demo-flower-cut-lily",
        name: "百合",
      },
    ],
  },
  {
    id: "demo-flower-leaf",
    name: "叶材",
    child: [
      {
        id: "demo-flower-leaf-eucalyptus",
        name: "尤加利",
      },
      {
        id: "demo-flower-leaf-turtle",
        name: "龟背叶",
      },
    ],
  },
];

export default {
  components: {
    GlbAnchorCalibration,
    GlbAppearanceEditor,
  },
  data() {
    const modelValidator = (rule, value, callback) => {
      if (!value) {
        callback(
          new Error(
            this.modelSourceType === "url"
              ? "请先填写并应用 GLB 模型地址"
              : "请先选择 GLB 模型文件"
          )
        );
        return;
      }
      callback();
    };

    return {
      id: "",
      loading: false,
      submitting: false,
      submitAction: "",
      uploading: false,
      compressing: false,
      appearanceSaving: false,
      coverUploading: false,
      modelSourceType: "upload",
      compressionPreset: "standard",
      compressionModeDialogVisible: false,
      modelUrlInput: "",
      labelList: [],
      colorList: [],
      ossUploadData: {},
      pendingCompressionFile: null,
      modelUploadMeta: {
        originalSize: 0,
        compressedSize: 0,
        compressed: false,
        compressionPresetLabel: "",
      },
      form: {
        name: "",
        colorClassifyId: "",
        growthStage: "",
        classifyId: [],
        coverImage: "",
        modelFileName: "",
        modelFileSize: 0,
        modelFilePath: "",
        originalModelFilePath: "",
        modelConfigJson: "",
        modelColorAdjust: createDefaultGlbColorAdjust(),
        basicInfo: "",
        connotation: "",
        collocation: "",
        colorSuggestion: "",
        technicalSuggestion: "",
        allegory: "",
        compositionSuggestion: "",
      },
      rules: {
        name: [
          {
            required: true,
            message: "请输入花材名称",
            trigger: "blur",
          },
        ],
        classifyId: [
          {
            type: "array",
            required: true,
            message: "请选择花材分类",
            trigger: "change",
          },
        ],
        modelFilePath: [
          {
            validator: modelValidator,
            trigger: "change",
          },
        ],
        coverImage: [
          {
            required: true,
            message: "请先上传缩略图",
            trigger: "change",
          },
        ],
      },
    };
  },
  computed: {
    selectedClassifyText() {
      if (!this.form.classifyId.length || !this.labelList.length) {
        return "";
      }
      const first = this.labelList.find((item) => item.id === this.form.classifyId[0]);
      if (!first) {
        return "";
      }
      const second = (first.child || []).find(
        (item) => item.id === this.form.classifyId[1]
      );
      return second ? `${first.name} / ${second.name}` : first.name;
    },
    modelSizeText() {
      if (!this.form.modelFileSize) {
        return "文件大小待定";
      }
      if (
        this.modelUploadMeta.compressed &&
        this.modelUploadMeta.originalSize &&
        this.modelUploadMeta.compressedSize
      ) {
        const compressedSize = this.modelUploadMeta.compressedSize / 1024 / 1024;
        const originalSize = this.modelUploadMeta.originalSize / 1024 / 1024;
        const savedRatio = Math.max(
          0,
          100 -
            (this.modelUploadMeta.compressedSize / this.modelUploadMeta.originalSize) *
              100
        );
        return `压缩后 ${compressedSize.toFixed(2)} MB / 原始 ${originalSize.toFixed(2)} MB / 减少 ${savedRatio.toFixed(0)}%`;
      }
      const size = this.form.modelFileSize / 1024 / 1024;
      return `${size.toFixed(2)} MB`;
    },
    uploadStatusText() {
      if (this.compressing) {
        return "正在压缩 GLB";
      }
      if (this.uploading) {
        return "正在上传到 OSS";
      }
      if (this.form.modelFilePath) {
        return this.modelSourceType === "url" ? "地址已应用" : "上传完成";
      }
      if (this.form.modelFileName) {
        return this.modelSourceType === "url" ? "地址待应用" : "等待重新上传";
      }
      return this.modelSourceType === "url" ? "请填写模型地址" : "请选择文件";
    },
    compressionPresetOptions() {
      return GLB_COMPRESSION_PRESETS;
    },
    currentCompressionPreset() {
      return getGlbCompressionPreset(this.compressionPreset);
    },
    prettyModelConfigJson() {
      return this.normalizeModelConfigJson(this.form.modelConfigJson);
    },
    isDefaultModelAppearance() {
      return isDefaultGlbColorAdjust(this.form.modelColorAdjust);
    },
    modelAppearanceSummary() {
      const adjust = normalizeGlbColorAdjust(this.form.modelColorAdjust);
      return [
        { label: "亮度", value: `${Math.round(adjust.brightness)}%` },
        { label: "补光", value: `${Math.round(adjust.emissiveStrength)}%` },
        { label: "补光色", value: adjust.emissiveColor || "-" },
      ];
    },
  },
  created() {
    this.id = this.$route.query.id || "";
    this.getLabelList();
    this.getColorList();
    this.getKey();
    if (this.id) {
      this.getDetail();
    }
  },
  methods: {
    getDetail() {
      this.loading = true;
      this.$ajax
        .get("/admin/materialManage/getById?id=" + this.id)
        .then((res) => {
          const resData = res.data || {};
          const detailData = this.parseDetailJson(resData.detail);
          this.form.name = resData.name || "";
          this.form.colorClassifyId = resData.colorClassifyId || "";
          this.form.growthStage = resData.growthStage || "";
          this.form.classifyId = [resData.classifyId2, resData.classifyId3].filter(
            Boolean
          );
          this.form.coverImage = resData.coverImage || "";
          this.form.modelFilePath = this.formatModelFilePath(
            resData.modelFilePath || ""
          );
          this.form.originalModelFilePath = this.parseAppearanceSourceModelPath(
            detailData,
            this.form.modelFilePath
          );
          this.form.modelFileName = this.getModelFileName(this.form.modelFilePath);
          this.modelUrlInput = this.form.modelFilePath;
          this.form.modelFileSize = 0;
          this.modelUploadMeta = {
            originalSize: 0,
            compressedSize: 0,
            compressed: false,
            compressionPresetLabel: "",
          };
          this.form.modelColorAdjust = this.parseDetailColorAdjust(detailData);
          this.form.basicInfo = resData.basicInfo || "";
          this.form.connotation = resData.connotation || "";
          this.form.collocation = resData.collocation || "";
          this.form.colorSuggestion = resData.colorSuggestion || "";
          this.form.technicalSuggestion = resData.technicalSuggestion || "";
          this.form.allegory = resData.allegory || "";
          this.form.compositionSuggestion = resData.compositionSuggestion || "";
          this.form.modelConfigJson = this.normalizeModelConfigJson(
            resData.modelConfigJson ||
              resData.modelConfig ||
              (detailData && detailData.modelConfig) ||
              ""
          );
        })
        .catch(() => {
          this.$message.error("获取 GLB 花材详情失败");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getColorList() {
      this.$ajax
        .post("/admin/classifyManage/queryAll")
        .then((res) => {
          const colorLabel = res.data.find((item) => item.id === 4);
          this.colorList = colorLabel && colorLabel.child ? colorLabel.child : [];
        })
        .catch(() => {
          this.colorList = [];
        });
    },
    getLabelList() {
      this.loading = true;
      this.$ajax
        .post("/admin/classifyManage/queryAll")
        .then((res) => {
          const flowerLabel = res.data.find((item) => item.id === 1);
          this.labelList = flowerLabel && flowerLabel.child && flowerLabel.child.length
            ? flowerLabel.child
            : fallbackLabelList;
        })
        .catch(() => {
          this.labelList = fallbackLabelList;
          this.$message.warning("分类接口暂不可用，已切换为演示分类数据");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getKey() {
      this.$ajax
        .post("/user/oss/getKey?pathName=cover")
        .then((res) => {
          this.ossUploadData.cover = res.data;
        })
        .catch(() => {
          this.$message.error("缩略图上传配置加载失败，请刷新页面后重试");
        });
      this.$ajax
        .post("/user/oss/getKey?pathName=common")
        .then((res) => {
          this.ossUploadData.material = res.data;
        })
        .catch(() => {
          this.$message.error("模型上传配置加载失败，请刷新页面后重试");
        });
    },
    handleClassifyChange() {
      this.$refs.form.validateField("classifyId");
    },
    handleModelSourceTypeChange(type) {
      if (type === "url") {
        this.modelUrlInput = this.form.modelFilePath || this.modelUrlInput;
      }
    },
    handleCoverUpload(file) {
      if (this.coverUploading) {
        return false;
      }
      const fileName = (file.name || "").toLowerCase();
      const maxSize = 10 * 1024 * 1024;
      const isImage = [".png", ".jpg", ".jpeg"].some((suffix) =>
        fileName.endsWith(suffix)
      );
      if (!isImage) {
        this.$message.error("缩略图仅支持 png / jpg / jpeg");
        return false;
      }
      if (file.size > maxSize) {
        this.$message.error("缩略图大小不能超过 10M");
        return false;
      }
      if (!this.ossUploadData.cover) {
        this.$message.error("缩略图上传配置未加载完成，请稍后重试");
        return false;
      }
      this.coverUploading = true;
      this.$refs.form.clearValidate("coverImage");
      this.getUploadPromise(
        {
          file,
        },
        this.ossUploadData.cover
      )
        .then((res) => {
          this.form.coverImage = res.addr;
          this.$message.success("缩略图上传成功");
        })
        .catch(() => {
          this.form.coverImage = "";
          this.$message.error("缩略图上传失败，请重试");
        })
        .finally(() => {
          this.coverUploading = false;
          this.$refs.form.validateField("coverImage");
        });
      return false;
    },
    handleModelFileUpload(file) {
      if (this.uploading || this.compressing) {
        return false;
      }
      const fileName = file.name || "";
      const lowerName = fileName.toLowerCase();
      const maxSize = 200 * 1024 * 1024;
      const compressThreshold = 10 * 1024 * 1024;
      if (!lowerName.endsWith(".glb")) {
        this.$message.error("当前仅支持选择 .glb 模型文件");
        return false;
      }
      if (file.size > maxSize) {
        this.$message.error("glb 文件大小不能超过 200M");
        return false;
      }
      if (!this.ossUploadData.material) {
        this.$message.error("上传配置未加载完成，请稍后重试");
        return false;
      }
      if (file.size > compressThreshold) {
        this.$confirm(
          `当前模型 ${(file.size / 1024 / 1024).toFixed(
            2
          )} MB，已超过 10MB。是否先选择压缩模式，再压缩后上传？`,
          "大文件处理",
          {
            confirmButtonText: "压缩后上传",
            cancelButtonText: "直接上传原文件",
            distinguishCancelAndClose: true,
            closeOnClickModal: false,
            type: "warning",
          }
        )
          .then(() => this.openCompressionModeDialog(file))
          .catch((action) => {
            if (action === "cancel") {
              this.uploadPreparedModelFile(file, {
                compressed: false,
                originalSize: file.size || 0,
                compressedSize: 0,
              });
            }
          });
        return false;
      }
      this.uploadPreparedModelFile(file, {
        compressed: false,
        originalSize: file.size || 0,
        compressedSize: 0,
      });
      return false;
    },
    openCompressionModeDialog(file) {
      this.pendingCompressionFile = file;
      this.compressionModeDialogVisible = true;
    },
    cancelCompressionModeDialog() {
      this.compressionModeDialogVisible = false;
      this.pendingCompressionFile = null;
    },
    confirmCompressionModeDialog() {
      if (!this.pendingCompressionFile) {
        this.compressionModeDialogVisible = false;
        return;
      }
      const file = this.pendingCompressionFile;
      this.pendingCompressionFile = null;
      this.compressionModeDialogVisible = false;
      this.compressAndUploadModelFile(file);
    },
    async compressAndUploadModelFile(file) {
      try {
        const preset = this.currentCompressionPreset;
        this.compressing = true;
        this.$refs.form.clearValidate("modelFilePath");
        const compressedFile = await compressGlbFile(file, preset);
        const compressedSize = compressedFile.size || 0;
        if (compressedSize >= file.size) {
          this.compressing = false;
          this.$confirm(
            `压缩后文件大小为 ${(compressedSize / 1024 / 1024).toFixed(2)} MB，没有比原文件更小。是否直接上传原文件？`,
            "压缩结果提示",
            {
              confirmButtonText: "上传原文件",
              cancelButtonText: "取消",
              distinguishCancelAndClose: true,
              closeOnClickModal: false,
              type: "warning",
            }
          )
            .then(() => {
              this.uploadPreparedModelFile(file, {
                compressed: false,
                originalSize: file.size || 0,
                compressedSize: 0,
              });
            })
            .catch(() => {});
          return;
        }
        this.compressing = false;
        this.uploadPreparedModelFile(compressedFile, {
          compressed: true,
          originalSize: file.size || 0,
          compressedSize,
          compressionPresetLabel: preset.label,
        });
      } catch (error) {
        this.compressing = false;
        const message =
          (error && error.message) ||
          "当前模型暂不支持浏览器内压缩，可直接上传原文件";
        this.$confirm(`${message}，是否直接上传原文件？`, "压缩失败", {
          confirmButtonText: "上传原文件",
          cancelButtonText: "取消",
          distinguishCancelAndClose: true,
          closeOnClickModal: false,
          type: "warning",
        })
          .then(() => {
            this.uploadPreparedModelFile(file, {
              compressed: false,
              originalSize: file.size || 0,
              compressedSize: 0,
            });
          })
          .catch(() => {});
      }
    },
    uploadPreparedModelFile(file, meta = {}) {
      this.form.modelFileName = file.name || "";
      this.form.modelFileSize = file.size || 0;
      this.form.modelFilePath = "";
      this.form.originalModelFilePath = "";
      this.modelUrlInput = "";
      this.form.modelConfigJson = "";
      this.form.modelColorAdjust = createDefaultGlbColorAdjust();
      this.modelUploadMeta = {
        originalSize: meta.originalSize || file.size || 0,
        compressedSize: meta.compressedSize || 0,
        compressed: !!meta.compressed,
        compressionPresetLabel: meta.compressionPresetLabel || "",
      };
      this.uploading = true;
      this.$refs.form.clearValidate("modelFilePath");
      this.getUploadPromise(
        {
          file,
        },
        this.ossUploadData.material
      )
        .then((res) => {
          this.form.modelFilePath = this.formatModelFilePath(res.addr);
          this.form.originalModelFilePath = this.form.modelFilePath;
          this.modelUrlInput = this.form.modelFilePath;
          this.$message.success(
            this.modelUploadMeta.compressed
              ? `模型已按${this.modelUploadMeta.compressionPresetLabel || "当前策略"}压缩并上传成功，请继续完成三点标定`
              : "模型文件上传成功，请继续完成三点标定"
          );
        })
        .catch(() => {
          this.form.modelFilePath = "";
          this.modelUrlInput = "";
          this.$message.error("模型文件上传失败，请重试");
        })
        .finally(() => {
          this.uploading = false;
          this.$refs.form.validateField("modelFilePath");
        });
    },
    applyModelUrl() {
      const rawValue = (this.modelUrlInput || "").trim();
      if (!rawValue) {
        this.$message.warning("请先输入 OSS 模型地址");
        return;
      }
      if (!/\.glb(\?.*)?$/i.test(rawValue)) {
        this.$message.error("模型地址必须是 .glb 文件");
        return;
      }
      const formattedPath = this.formatModelFilePath(rawValue);
      const hasChanged = formattedPath !== this.form.modelFilePath;
      this.form.modelFilePath = formattedPath;
      this.form.originalModelFilePath = formattedPath;
      this.form.modelFileName = this.getModelFileName(formattedPath);
      this.form.modelFileSize = 0;
      this.modelUploadMeta = {
        originalSize: 0,
        compressedSize: 0,
        compressed: false,
        compressionPresetLabel: "",
      };
      this.modelUrlInput = formattedPath;
      if (hasChanged) {
        this.form.modelConfigJson = "";
        this.form.modelColorAdjust = createDefaultGlbColorAdjust();
      }
      this.$refs.form.validateField("modelFilePath");
      this.$message.success(
        hasChanged ? "模型地址已应用，请继续完成三点标定" : "模型地址已更新"
      );
    },
    parseDetailColorAdjust(detailData) {
      const source =
        (detailData &&
          ((detailData.modelAppearance && detailData.modelAppearance.colorAdjust) ||
            (detailData.renderProfile && detailData.renderProfile.colorAdjust) ||
            detailData.colorAdjust)) ||
        {};
      return normalizeGlbColorAdjust(source);
    },
    parseAppearanceSourceModelPath(detailData, fallbackPath = "") {
      const sourcePath =
        (detailData &&
          ((detailData.modelAppearance &&
            detailData.modelAppearance.sourceModelFilePath) ||
            detailData.sourceModelFilePath ||
            detailData.originalModelFilePath)) ||
        fallbackPath;
      return this.formatModelFilePath(sourcePath || "");
    },
    openAppearanceEditor() {
      if (!this.form.modelFilePath) {
        this.$message.warning("请先上传 GLB 文件");
        return;
      }
      this.$refs.glbAppearanceEditor.open({
        resourceKey:
          this.id ||
          this.form.originalModelFilePath ||
          this.form.modelFilePath,
        resourceName: this.form.name || this.form.modelFileName || "GLB花材",
        modelUrl: this.form.originalModelFilePath || this.form.modelFilePath,
        sourceFileName: this.getModelFileName(
          this.form.originalModelFilePath || this.form.modelFilePath
        ),
        modelColorAdjust: this.form.modelColorAdjust,
      });
    },
    async handleAppearanceConfirm(payload) {
      if (!payload || !payload.bakedFile) {
        this.$message.error("GLB 光感烘焙失败，请重试");
        return;
      }
      if (!this.ossUploadData.material) {
        this.$message.error("模型上传配置未加载完成，请稍后重试");
        return;
      }
      const nextColorAdjust = normalizeGlbColorAdjust(
        payload.colorAdjust || payload.colorAdjustJson
      );
      const sourceModelFilePath =
        this.form.originalModelFilePath || this.form.modelFilePath;
      this.appearanceSaving = true;
      try {
        let uploadFile = payload.bakedFile;
        let compressed = false;
        let compressedSize = 0;
        if (uploadFile.size > 10 * 1024 * 1024) {
          try {
            const compressedFile = await compressGlbFile(
              uploadFile,
              this.currentCompressionPreset
            );
            if (compressedFile.size < uploadFile.size) {
              uploadFile = compressedFile;
              compressed = true;
              compressedSize = compressedFile.size || 0;
            }
          } catch (error) {
            compressed = false;
            compressedSize = 0;
          }
        }
        const res = await this.getUploadPromise(
          {
            file: uploadFile,
          },
          this.ossUploadData.material
        );
        this.form.modelColorAdjust = nextColorAdjust;
        this.form.modelFilePath = this.formatModelFilePath(res.addr);
        this.form.originalModelFilePath = this.formatModelFilePath(
          sourceModelFilePath
        );
        this.form.modelFileName = this.getModelFileName(this.form.modelFilePath);
        this.form.modelFileSize = uploadFile.size || 0;
        this.modelUrlInput = this.form.modelFilePath;
        this.modelUploadMeta = {
          originalSize: uploadFile.size || 0,
          compressedSize,
          compressed,
          compressionPresetLabel: compressed
            ? this.currentCompressionPreset.label
            : "",
        };
        this.$message.success("GLB 默认光感已烘焙进模型并上传成功");
      } catch (error) {
        console.error("GLB appearance upload failed:", error);
        this.$message.error(
          (error && error.message) || "GLB 光感烘焙上传失败，请重试"
        );
      } finally {
        this.appearanceSaving = false;
      }
    },
    resetModelAppearance() {
      this.form.modelColorAdjust = createDefaultGlbColorAdjust();
      this.$message.success("已恢复为默认光感参数");
    },
    formatModelFilePath(path) {
      if (!path) {
        return "";
      }
      if (/^https?:\/\//.test(path)) {
        return path;
      }
      return (
        "https://lvshuhualv.oss-cn-hangzhou.aliyuncs.com/" +
        path.replace(/^\/+/, "")
      );
    },
    normalizeModelConfigJson(input) {
      if (!input) {
        return "";
      }
      try {
        const modelConfig = typeof input === "string" ? JSON.parse(input) : { ...input };
        const supportedTools = Array.isArray(modelConfig.supportedTools)
          ? Array.from(new Set([...modelConfig.supportedTools, "directionRotate"]))
          : ["directionRotate"];
        const getNumberOrDefault = (value, fallback) => {
          const parsed = parseFloat(value);
          return Number.isNaN(parsed) ? fallback : parsed;
        };
        return JSON.stringify(
          {
            ...modelConfig,
            supportedTools,
            headAngleStep: getNumberOrDefault(modelConfig.headAngleStep, 15),
            headAngleMin: getNumberOrDefault(modelConfig.headAngleMin, -180),
            headAngleMax: getNumberOrDefault(modelConfig.headAngleMax, 180),
          },
          null,
          2
        );
      } catch (error) {
        return typeof input === "string" ? input : "";
      }
    },
    parseDetailJson(detail) {
      if (!detail) {
        return null;
      }
      try {
        return typeof detail === "string" ? JSON.parse(detail) : detail;
      } catch (error) {
        return null;
      }
    },
    getModelFileName(path) {
      if (!path) {
        return "";
      }
      const cleanPath = path.split("?")[0];
      return decodeURIComponent(cleanPath.split("/").pop() || "");
    },
    openCalibration() {
      if (!this.form.modelFilePath) {
        this.$message.warning("请先上传 GLB 文件");
        return;
      }
      this.$refs.glbCalibration.open({
        resourceKey: this.id || this.form.modelFilePath,
        resourceName: this.form.name || this.form.modelFileName || "GLB花材",
        modelUrl: this.form.modelFilePath,
        modelConfigJson: this.form.modelConfigJson,
      });
    },
    handleCalibrationConfirm(payload) {
      this.form.modelConfigJson = this.normalizeModelConfigJson(
        payload.modelConfigJson || payload.modelConfig
      );
      this.$message.success("GLB 三点标定已保存到当前表单");
    },
    clearCalibration() {
      this.form.modelConfigJson = "";
      this.$message.success("已清空当前标定参数");
    },
    removeCover() {
      this.coverUploading = false;
      this.form.coverImage = "";
      this.$refs.form.validateField("coverImage");
    },
    buildGlbDetailPayload() {
      const parsedModelConfig = this.parseDetailJson(this.form.modelConfigJson) || {};
      const colorAdjust = normalizeGlbColorAdjust(this.form.modelColorAdjust);
      const sourceModelFilePath =
        this.form.originalModelFilePath || this.form.modelFilePath;
      return JSON.stringify({
        format: "glb",
        url: this.form.modelFilePath,
        sourceModelFilePath,
        defaultScale: 90,
        stem: {
          color: "#4d6840",
          radiusStem: 2,
        },
        flower: {
          radiusTop: "18",
          radiusBottom: "18",
          height: "28",
          color: "#6d8f5b",
        },
        modelConfig: parsedModelConfig,
        colorAdjust,
        modelAppearance: {
          version: 1,
          bakedIntoModel: true,
          sourceModelFilePath,
          colorAdjust,
        },
      });
    },
    buildPayload(status) {
      return {
        name: this.form.name,
        growthStage: this.form.growthStage,
        colorClassifyId: this.form.colorClassifyId,
        classifyId1: 1,
        classifyId2: this.form.classifyId[0] || "",
        classifyId3: this.form.classifyId[1] || "",
        modelFilePath: this.form.modelFilePath,
        modelConfigJson: this.form.modelConfigJson,
        coverImage: this.form.coverImage,
        basicInfo: this.form.basicInfo,
        connotation: this.form.connotation,
        collocation: this.form.collocation,
        colorSuggestion: this.form.colorSuggestion,
        technicalSuggestion: this.form.technicalSuggestion,
        allegory: this.form.allegory,
        compositionSuggestion: this.form.compositionSuggestion,
        detail: this.buildGlbDetailPayload(),
        status,
        ids: this.id ? [this.id] : "",
      };
    },
    publishToFlowerList() {
      const requestUrl = this.id
        ? "/admin/materialManage/batchUpdate"
        : "/admin/materialManage/save";
      this.submitting = true;
      this.submitAction = "publish";
      this.$ajax
        .post(requestUrl, this.buildPayload(1))
        .then(() => {
          this.$message.success(this.id ? "修改并发布成功" : "发布成功");
          this.$router.push({
            path: "/flower/list",
            query: {
              name: this.form.name,
            },
          });
        })
        .catch(() => {
          this.$message.error("发布失败，请重试");
        })
        .finally(() => {
          this.submitting = false;
          this.submitAction = "";
        });
    },
    removeModel() {
      this.uploading = false;
      this.compressing = false;
      this.form.modelFileName = "";
      this.form.modelFileSize = 0;
      this.form.modelFilePath = "";
      this.form.originalModelFilePath = "";
      this.modelUrlInput = "";
      this.form.modelConfigJson = "";
      this.form.modelColorAdjust = createDefaultGlbColorAdjust();
      this.modelUploadMeta = {
        originalSize: 0,
        compressedSize: 0,
        compressed: false,
        compressionPresetLabel: "",
      };
      this.$refs.form.validateField("modelFilePath");
    },
    triggerFilePicker() {
      const input = this.$el.querySelector(".upload-dropzone input[type='file']");
      input && input.click();
    },
    resetForm() {
      this.uploading = false;
      this.compressing = false;
      this.appearanceSaving = false;
      this.coverUploading = false;
      this.modelUploadMeta = {
        originalSize: 0,
        compressedSize: 0,
        compressed: false,
        compressionPresetLabel: "",
      };
      this.compressionPreset = "standard";
      this.form = {
        name: "",
        colorClassifyId: "",
        growthStage: "",
        classifyId: [],
        coverImage: "",
        modelFileName: "",
        modelFileSize: 0,
        modelFilePath: "",
        originalModelFilePath: "",
        modelConfigJson: "",
        modelColorAdjust: createDefaultGlbColorAdjust(),
        basicInfo: "",
        connotation: "",
        collocation: "",
        colorSuggestion: "",
        technicalSuggestion: "",
        allegory: "",
        compositionSuggestion: "",
      };
      this.modelSourceType = "upload";
      this.modelUrlInput = "";
      this.$nextTick(() => {
        this.$refs.form.clearValidate();
      });
    },
    handleSubmit(action) {
      if (
        this.uploading ||
        this.compressing ||
        this.coverUploading ||
        this.appearanceSaving
      ) {
        this.$message.warning("素材文件处理中，请稍候再提交");
        return;
      }
      this.$refs.form.validate((valid) => {
        if (!valid) {
          this.$message.warning("请先补全名称、分类、缩略图和模型文件");
          return;
        }
        if (action === "publish" && !this.form.modelConfigJson) {
          this.$message.warning("请先完成 GLB 三点标定，再执行发布");
          this.openCalibration();
          return;
        }
        if (action === "publish") {
          this.publishToFlowerList();
          return;
        }
        this.$message.success("保存草稿成功，当前仍未写入花材列表");
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.glb-upload-page {
  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
  }

  .page-subtitle {
    font-size: 14px;
    color: #909399;
    line-height: 1.6;
  }

  .page-alert {
    margin-bottom: 20px;
  }

  .content-grid {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr);
    gap: 20px;
    margin-bottom: 24px;
  }

  .form-card,
  .preview-card {
    border-radius: 12px;
  }

  .extra-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .extra-section-title {
    font-size: 16px;
    font-weight: 600;
    color: #606266;
    margin-top: 4px;
  }

  .extra-form {
    padding: 14px 16px 6px;
    border-radius: 12px;
    background: #f8faf4;
  }

  .card-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .full-width {
    width: 100%;
  }

  .color-radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
  }

  .color-dot {
    width: 10px;
    height: 10px;
    display: inline-block;
    border-radius: 999px;
    margin-right: 6px;
    vertical-align: middle;
  }

  .upload-dropzone {
    width: 100%;
  }

  .model-source-switch {
    margin-bottom: 12px;
  }

  .model-url-panel {
    border: 1px solid #ebeef5;
    border-radius: 12px;
    padding: 16px;
    background: #fafcf6;
  }

  .model-url-tip {
    margin-top: 10px;
    font-size: 12px;
    color: #909399;
    line-height: 1.6;
  }

  .appearance-panel {
    padding: 14px 16px;
    border-radius: 12px;
    background: #f8faf4;
  }

  .appearance-panel__status {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;
  }

  .appearance-panel__text {
    font-size: 12px;
    line-height: 1.7;
    color: #909399;
  }

  .appearance-panel__actions {
    display: flex;
    gap: 10px;
    margin-bottom: 12px;
  }

  .appearance-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .dropzone-empty,
  .dropzone-filled {
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dropzone-empty {
    flex-direction: column;
    color: #909399;
  }

  .upload-icon {
    font-size: 44px;
    color: #8bb229;
    margin-bottom: 12px;
  }

  .upload-title {
    font-size: 15px;
    color: #606266;
    margin-bottom: 8px;
  }

  .upload-desc {
    font-size: 13px;
    color: #909399;
  }

  .dropzone-filled {
    text-align: left;
    justify-content: space-between;
    padding: 0 24px;
    gap: 16px;
  }

  .file-icon {
    font-size: 40px;
    color: #8bb229;
  }

  .file-meta {
    flex: 1;
  }

  .file-name {
    font-size: 15px;
    color: #303133;
    word-break: break-all;
    margin-bottom: 8px;
  }

  .file-desc {
    font-size: 13px;
    color: #909399;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .file-actions {
    display: flex;
    gap: 16px;
    margin-top: 8px;
  }

  .danger-text {
    color: #f56c6c;
  }

  .upload-progress {
    margin-top: 12px;
    color: #909399;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .uploaded-path {
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #f5f7fa;
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .uploaded-path__label {
    flex-shrink: 0;
    font-size: 12px;
    color: #909399;
    line-height: 1.6;
  }

  .uploaded-path__value {
    font-size: 12px;
    color: #606266;
    line-height: 1.6;
    word-break: break-all;
  }

  .compression-config {
    margin-top: 14px;
    padding: 12px 14px;
    border-radius: 10px;
    background: #f8faf4;
  }

  .compression-config__label {
    font-size: 13px;
    font-weight: 600;
    color: #606266;
    margin-bottom: 10px;
  }

  .compression-config__desc {
    margin-top: 10px;
    font-size: 12px;
    color: #909399;
    line-height: 1.6;
  }

  .compression-dialog-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .compression-dialog-tip {
    font-size: 14px;
    color: #4c5a4c;
    line-height: 1.7;
  }

  .compression-dialog-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .compression-dialog-option {
    margin-right: 0;
    width: 100%;
    height: auto;
    padding: 12px 14px;
  }

  .compression-dialog-option ::v-deep .el-radio__label {
    display: inline-flex;
    flex-direction: column;
    gap: 4px;
    white-space: normal;
  }

  .compression-dialog-option__title {
    font-size: 14px;
    font-weight: 600;
    color: #213021;
  }

  .compression-dialog-option__desc {
    font-size: 12px;
    line-height: 1.6;
    color: #6e7c6e;
  }

  .cover-upload-row {
    display: flex;
    gap: 16px;
    align-items: stretch;
  }

  .cover-upload {
    flex-shrink: 0;
  }

  .cover-empty,
  .cover-filled {
    position: relative;
    width: 180px;
    height: 180px;
    border-radius: 14px;
    overflow: hidden;
    border: 1px dashed #d8dfc1;
    background: linear-gradient(180deg, #fbfcf7 0%, #f3f8e7 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    cursor: pointer;
  }

  .cover-empty {
    flex-direction: column;
    color: #8f9b6a;
    padding: 18px;
    box-sizing: border-box;
  }

  .cover-empty i {
    font-size: 34px;
    margin-bottom: 10px;
  }

  .cover-empty__title {
    font-size: 15px;
    color: #55612b;
    margin-bottom: 8px;
  }

  .cover-empty__desc {
    font-size: 12px;
    color: #7d8760;
    line-height: 1.6;
  }

  .cover-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .cover-mask {
    position: absolute;
    inset: auto 0 0 0;
    padding: 10px 12px;
    font-size: 12px;
    color: #fff;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.55) 100%);
  }

  .cover-side {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 180px;
    color: #606266;
  }

  .cover-side__title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
  }

  .cover-side__desc,
  .cover-side__status {
    font-size: 13px;
    color: #909399;
    line-height: 1.7;
  }

  .calibration-panel {
    padding: 12px 14px;
    border-radius: 10px;
    background: #f7f9ef;
    border: 1px solid #e3ebc7;
  }

  .calibration-status {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .calibration-status__text {
    font-size: 12px;
    color: #6b6a55;
    line-height: 1.6;
  }

  .calibration-actions {
    display: flex;
    gap: 10px;
    margin-top: 12px;
  }

  .model-config-preview {
    margin-top: 14px;

    ::v-deep .el-textarea__inner {
      font-family: Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
      line-height: 1.6;
      color: #606266;
      background: #fff;
    }
  }

  .preview-panel {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .preview-label {
    font-size: 12px;
    color: #909399;
  }

  .preview-value {
    font-size: 15px;
    color: #303133;
    min-height: 22px;
    margin-bottom: 4px;
    word-break: break-word;
  }

  .preview-thumb {
    margin-bottom: 8px;
  }

  .preview-thumb__image,
  .preview-thumb__empty {
    width: 100%;
    max-width: 240px;
    height: 160px;
    border-radius: 12px;
    border: 1px solid #ebeef5;
  }

  .preview-thumb__image {
    object-fit: cover;
    display: block;
    background: #f5f7fa;
  }

  .preview-thumb__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;
    box-sizing: border-box;
    font-size: 12px;
    color: #a0a7b1;
    background: linear-gradient(180deg, #fafbfc 0%, #f5f7fa 100%);
    text-align: center;
    line-height: 1.6;
  }

  .preview-path {
    font-size: 13px;
    color: #606266;
  }

  .preview-box {
    margin-top: 16px;
    padding: 28px 18px;
    border: 1px dashed #dcdfe6;
    border-radius: 12px;
    background: linear-gradient(180deg, #fbfcf7 0%, #f7f9ef 100%);
    text-align: center;
  }

  .preview-box i {
    font-size: 40px;
    color: #8bb229;
    margin-bottom: 12px;
    display: inline-block;
  }

  .preview-box-title {
    font-size: 15px;
    color: #303133;
    margin-bottom: 8px;
    word-break: break-word;
  }

  .preview-box-desc {
    font-size: 13px;
    color: #909399;
    line-height: 1.6;
  }

  .actions {
    display: flex;
    gap: 12px;
  }
}

@media (max-width: 1080px) {
  .glb-upload-page {
    .content-grid {
      grid-template-columns: 1fr;
    }

    .cover-upload-row {
      flex-direction: column;
      align-items: flex-start;
    }
  }
}
</style>
